import { getLogger } from '../core/logger.js';
import { SuspensionTracker } from '../core/logic/suspension-tracker.js';
import {
  foldCaption,
  type StateSource,
  type WidgetActions,
  type WidgetState,
} from '../core/logic/widget-state.js';

import type { BaseSDKService } from './BaseSDKService.js';
import type { SDKEventMap, SDKEventName } from './types.js';

/**
 * SDK data adapter for the widget seam: folds a service's event stream into an
 * immutable `WidgetState` and maps `WidgetActions` onto the service's methods.
 *
 * The service outlives the source — `destroy()` detaches the listeners but
 * leaves the service connected (the host owns its lifecycle).
 */
const logger = getLogger('SdkStateSource');

export function createSdkStateSource(service: BaseSDKService): StateSource {
  const snapshot = service.getState();
  let state: WidgetState = {
    mode: snapshot.serviceMode,
    captions: [],
    agentState: snapshot.agentState,
    micStatus: snapshot.micStatus,
    audioBlocked: !snapshot.canPlaybackAudio,
    dealerMuted: snapshot.muted,
    suspended: false,
    micJustEnabled: false,
    micAcquired: false,
  };

  const listeners = new Set<(next: WidgetState) => void>();
  const patch = (partial: Partial<WidgetState>): void => {
    const changed = (Object.keys(partial) as Array<keyof WidgetState>).some(
      (key) => partial[key] !== state[key]
    );
    if (!changed) return;
    state = { ...state, ...partial };
    for (const listener of listeners) {
      listener(state);
    }
  };

  const suspension = new SuspensionTracker({
    onChange: (isSuspended) => patch({ suspended: isSuspended }),
  });

  // ---- PTT turn guards (private — the renderer's gesture machine is the only
  // UI-facing PTT state). They guarantee start/end pairing at the agent:
  // no start_turn while a turn is open(ing); no end/cancel_turn without an
  // open turn; a gesture that ends while startPTT is suspended on the
  // mic-permission prompt closes the just-opened turn instead of leaking it.
  let starting = false;
  let turnOpen = false;
  let turnEpoch = 0;
  let closeWith: 'end' | 'cancel' = 'end';
  // Whether any startPTT has succeeded this session — the mismatch path arms
  // the micJustEnabled hint only for the FIRST one (the acquisition the
  // permission prompt rides). Later mismatches are ordinary short taps where
  // the hint would be redundant noise that hides captions.
  let acquiredOnce = false;

  // Typed handler map so every subscription has a matching removal in destroy().
  const handlers: { [K in SDKEventName]?: (payload: SDKEventMap[K]) => void } = {
    serviceMode: (mode) => patch({ mode }),
    agentState: (agentState) => patch({ agentState }),
    // The pill is dealer subtitles: the player's own STT is not echoed back.
    // (The raw SDK 'transcription' event still carries both speakers for
    // consumers that want them.)
    transcription: (segment) => {
      if (segment.speaker !== 'dealer') return;
      // A dealer caption ends the hint's moment: captions get the pill back.
      patch({ captions: foldCaption(state.captions, segment), micJustEnabled: false });
    },
    mute: ({ muted }) => patch({ dealerMuted: muted }),
    audioPlaybackChanged: ({ canPlayback }) => patch({ audioBlocked: !canPlayback }),
    micStatus: ({ status }) => patch({ micStatus: status }),
    gameEventSent: (event) => suspension.handleEvent(event),
    // agentState 'disconnected' arrives via its own event; a mid-hold transport
    // drop additionally means the turn died with the room — nothing to send.
    disconnected: () => {
      turnEpoch += 1;
      turnOpen = false;
    },
  };
  for (const [name, handler] of Object.entries(handlers)) {
    service.on(name as SDKEventName, handler as (payload: SDKEventMap[SDKEventName]) => void);
  }

  const actions: WidgetActions = {
    async startPTT(): Promise<void> {
      if (starting || turnOpen) {
        // Investigation instrumentation (#458 follow-up): this silent drop is
        // the "ghost hold" — the widget renders an active press that owns no
        // turn. Make it name itself.
        logger.debug('startPTT DROPPED — previous turn still in flight', { starting, turnOpen });
        return;
      }
      patch({ micJustEnabled: false }); // the player is holding again
      starting = true;
      const epoch = turnEpoch;
      try {
        await service.acquireMic();
        const firstAcquire = !acquiredOnce;
        acquiredOnce = true;
        patch({ micAcquired: true });

        if (turnEpoch !== epoch) {
          // The press ended while acquisition was suspended on the permission
          // prompt. Open NO turn and send nothing: a start_turn here would
          // reach the agent as "the player is talking" and interrupt whatever
          // the dealer is saying by now, truncating it (#475). The mic goes
          // back too — capture is held only while a turn is open.
          logger.debug('press ended before the mic was ready — no turn opened');
          await service.releaseMic().catch(() => {
            /* best effort — the next hold re-acquires either way */
          });
          if (firstAcquire) {
            // The session's first acquisition means the mic WAS just granted.
            // Say so, or the grant looks like it did nothing (#458). Cleared by
            // the next hold or the dealer's next caption.
            patch({ micJustEnabled: true });
          }
          return;
        }

        await service.openTurn();
        if (turnEpoch === epoch) {
          turnOpen = true;
        } else {
          // The press ended inside the start_turn round-trip. Unlike the
          // permission window above, this turn IS open at the agent, so it has
          // to be closed the way the gesture ended (release → end, swipe →
          // cancel).
          logger.debug('press ended during start_turn — closing the turn it opened', {
            closeWith,
          });
          await (closeWith === 'cancel' ? service.cancelPTT() : service.endPTT());
        }
      } catch (err) {
        // micStatus still 'ok' after a failed startPTT means acquisition
        // succeeded and the turn RPC failed — the grant survives the failure.
        // (micStatus initializes optimistically to 'ok'; the pre-acquire-throw
        // window is guard-bounded — the f57240f residual, now owned here, the
        // single home of this truth.)
        if (state.micStatus === 'ok') {
          acquiredOnce = true;
          patch({ micAcquired: true });
        }
        throw err;
      } finally {
        starting = false;
      }
    },
    async endPTT(): Promise<void> {
      turnEpoch += 1;
      closeWith = 'end';
      if (!turnOpen) return; // no open turn — never send a stray end_turn
      turnOpen = false;
      await service.endPTT();
    },
    async cancelPTT(): Promise<void> {
      turnEpoch += 1;
      closeWith = 'cancel';
      if (!turnOpen) return;
      turnOpen = false;
      await service.cancelPTT();
    },
    startAudio(): Promise<void> {
      // audioBlocked clears via the SDK's audioPlaybackChanged event.
      return service.startAudio();
    },
    toggleMute(): Promise<void> {
      return service.setDealerMute(!state.dealerMuted);
    },
    sendText(text: string): Promise<void> {
      return service.sendText(text);
    },
  };

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    actions,
    destroy(): void {
      for (const [name, handler] of Object.entries(handlers)) {
        service.off(name as SDKEventName, handler as (payload: SDKEventMap[SDKEventName]) => void);
      }
      listeners.clear();
    },
  };
}
